import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography } from "@mui/material";
import axios from "axios";
import { FormEvent, useState } from "react";
import { IAssignable } from "../types";

import { deadCheckReasons } from "../deadChecks/types";

interface IProps {
  entity: IAssignable;
  onClose: () => void;
  onSaved: () => void;
}

export default function MarkDeadModal({ entity, onClose, onSaved }: IProps) {
  const [fields, setFields] = useState({
    street: "",
    apt: "",
    city: "",
    state: "",
    zip: "",
    taxIdDocumentUrl: "",
    dateOfLastActivity: "",
    reason: "",
    contactEmail: "",
    contactPhone: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const updateField = (name: keyof typeof fields, value: string) => {
    setFields((current) => ({ ...current, [name]: value }));
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await axios.post("/api/OutstandingChecks/MarkDead", {
        ...Object.fromEntries(Object.entries(fields).map(([key, value]) => [key, value.trim() || null])),
        uniqueIdentifier: entity.uniqueIdentifier,
        reason: fields.reason === "" ? null : Number(fields.reason)
      });
    } catch (error) {
      setError(axios.isAxiosError(error) && typeof error.response?.data === "string"
        ? error.response.data
        : "Unable to mark this check dead. Please try again.");
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
    onSaved();
  };

  return (
    <Dialog open onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="mark-dead-title">
      <Box component="form" onSubmit={save}>
        <DialogTitle id="mark-dead-title">Mark check dead — {entity.orderNumber}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <Typography variant="subtitle2">Last known address of the payee</Typography>
            <Typography variant="body2" color="text.secondary">Leave unknown address and contact details blank.</Typography>
            <TextField label="Street" value={fields.street} onChange={(e) => updateField("street", e.target.value)} fullWidth />
            <TextField label="Apt" value={fields.apt} onChange={(e) => updateField("apt", e.target.value)} fullWidth />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr 1fr" }, gap: 2 }}>
              <TextField label="City" value={fields.city} onChange={(e) => updateField("city", e.target.value)} />
              <TextField label="State" value={fields.state} onChange={(e) => updateField("state", e.target.value.toUpperCase())}
                inputProps={{ maxLength: 2, pattern: "[A-Za-z]{2}" }} />
              <TextField label="Zip" value={fields.zip} onChange={(e) => updateField("zip", e.target.value)} />
            </Box>
            <TextField label="Tax ID document URL" type="url" value={fields.taxIdDocumentUrl}
              onChange={(e) => updateField("taxIdDocumentUrl", e.target.value)}
              helperText="Link to an existing document in Momentum containing the SSN or EIN." fullWidth />
            <TextField label="Date of last activity" type="date" required value={fields.dateOfLastActivity}
              onChange={(e) => updateField("dateOfLastActivity", e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
            <TextField label="Reason" select required value={fields.reason}
              onChange={(e) => updateField("reason", e.target.value)} fullWidth>
              {deadCheckReasons.map((reason, index) => <MenuItem key={reason} value={String(index)}>{reason}</MenuItem>)}
            </TextField>
            <TextField label="Contact email" type="email" value={fields.contactEmail}
              onChange={(e) => updateField("contactEmail", e.target.value)} fullWidth />
            <TextField label="Contact phone #" type="tel" value={fields.contactPhone}
              onChange={(e) => updateField("contactPhone", e.target.value)} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={submitting || !fields.dateOfLastActivity || fields.reason === ""}>{submitting ? "Saving..." : "Mark dead"}</Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
